import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { SNIPPET_TYPE } from "../../constants";
import useSnippetLanguages from "../../hooks/useSnippetLanguages";
import { apiErrorMessage } from "../../services/apiError";

const TYPE_OPTIONS = [
  { value: SNIPPET_TYPE.PLAIN, label: "Plain text" },
  { value: SNIPPET_TYPE.MARKDOWN, label: "Markdown" },
  { value: SNIPPET_TYPE.CODE, label: "Code" },
];

// Shared create/edit form. Backed by `initialValue` so it can preload an
// existing snippet for editing. `onSubmit` returns a Promise — if it rejects,
// the form surfaces the message and keeps the user's input.
function SnippetForm({
  initialValue = null,
  submitLabel,
  onSubmit,
  onCancel = null,
}) {
  const [title, setTitle] = useState(initialValue?.title || "");
  const [body, setBody] = useState(initialValue?.body || "");
  const [snippetType, setSnippetType] = useState(
    initialValue?.snippet_type || SNIPPET_TYPE.PLAIN,
  );
  const [language, setLanguage] = useState(initialValue?.language || "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { languages, loading: languagesLoading } = useSnippetLanguages();

  // When `snippet_type` flips off code, clear language so the backend
  // cross-field rule doesn't reject the payload.
  useEffect(() => {
    if (snippetType !== SNIPPET_TYPE.CODE && language) {
      setLanguage("");
    }
  }, [snippetType, language]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const payload = {
      title,
      body,
      snippet_type: snippetType,
      language: snippetType === SNIPPET_TYPE.CODE ? language : "",
    };
    try {
      await onSubmit(payload);
    } catch (err) {
      setError(apiErrorMessage(err, "Save failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mb-4 grid gap-1.5">
        <Label htmlFor="snippet-title">Title (optional)</Label>
        <Input
          id="snippet-title"
          type="text"
          value={title}
          maxLength={255}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="A short label for this snippet"
        />
      </div>

      <div className="mb-4 grid gap-1.5">
        <Label htmlFor="snippet-type">Type</Label>
        <NativeSelect
          id="snippet-type"
          value={snippetType}
          onChange={(event) => setSnippetType(event.target.value)}
        >
          {TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </NativeSelect>
        <p className="text-sm text-muted-foreground">
          Markdown renders as formatted HTML; code renders with syntax
          highlighting using the chosen language.
        </p>
      </div>

      {snippetType === SNIPPET_TYPE.CODE && (
        <div className="mb-4 grid gap-1.5">
          <Label htmlFor="snippet-language">Language</Label>
          <NativeSelect
            id="snippet-language"
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            required
            disabled={languagesLoading}
          >
            <option value="" disabled>
              {languagesLoading ? "Loading languages…" : "Pick a language…"}
            </option>
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}

      <div className="mb-4 grid gap-1.5">
        <Label htmlFor="snippet-body">Body</Label>
        <Textarea
          id="snippet-body"
          className="max-h-96 font-mono"
          rows={14}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          required
          placeholder="Paste or type your snippet here…"
        />
      </div>

      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="animate-spin" />
              Saving...
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}

SnippetForm.propTypes = {
  initialValue: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    snippet_type: PropTypes.string,
    language: PropTypes.string,
  }),
  submitLabel: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default SnippetForm;
